import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import styled from '@emotion/styled'
import {
  Box,
  Button,
  Grid,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  SxProps,
  Typography
} from '@mui/material'
// disable sentry
// import { CONSOLE_LEVELS, consoleSandbox } from '@sentry/utils'
import { ColorBoxContent } from 'common/ui/colorBox'

import PlusIcon from '../../../../assets/icons/circles-design/plus.png'
import FulfilmentUploadDataGrid from '../components/FulfilmentUploadDataGrid'

const correctHeaders = [
  'firstName',
  'lastName',
  'userEmail',
  'countryCode',
  'mobileNo'
]

/** Dotted box used for uploads */
function DottedBox(props: IDottedBoxProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px dashed #B8C4D3',
        borderRadius: '8px',
        width: '20%',
        padding: '30px 35px 30px 30px'
      }}
    >
      {props.children}
    </Box>
  )
}

/** Styled div that bolds content. */
const StrongText = styled.div({
  display: 'block',
  fontWeight: 'bold',
  fontSize: 16
})

const stepIconSx: SxProps = {
  '.MuiStepIcon-root': {
    fill: 'black',
    fontWeight: 'bold'
  },
  '.Mui-active': {
    fill: 'blue',
    fontWeight: 'bold'
  }
}
export default function PreorderUpload() {
  const [uploadedFilename, setUploadedFilename] = useState<string>('')
  const [uploadReqBody, setUploadReqBody] = useState<IUploadReqBody>({
    preorders: []
  })
  const [uploadErr, setUploadErr] = useState<string>('')
  const [uploadStatus, setUploadStatus] = useState<string>('')
  const [rows, setRows] = useState<IFulfilmentRow[]>([])

  /** Helper function to parse CSV file. */
  function parseCsv(csvFile): IFulfilmentCSVData[] | null {
    const csvFileRows: string[] = csvFile.split('\n')
    const values: IFulfilmentCSVData[] = []
    const headers: string[] = []
    for (let i = 0; i < csvFileRows.length; i++) {
      if (i === 0) {
        csvFileRows[i]
          .replaceAll('"', '')
          .trim()
          .split(',')
          .forEach((value) => headers.push(value))
        let err = false
        headers.forEach((header, index) => {
          if (header !== correctHeaders[index]) {
            err = true
          }
        })
        if (err) {
          setUploadStatus('ERROR')
          setUploadErr('Invalid csv format, please check your column headers')
          return null
        }
      } else if (csvFileRows[i] === '') {
        continue
      } else {
        const row = {} as IFulfilmentCSVData
        csvFileRows[i]
          .replaceAll('"', '')
          .trim()
          .split(',')
          .forEach((value, index) => {
            row['id'] = i
            row[headers[index]] = value
          })
        values.push(row)
      }
    }
    setUploadStatus('PENDING')

    return values
  }

  const onFileUpload = async (file) => {
    const fileName = file.name
    const reader = new FileReader()

    reader.onload = async (e) => {
      const text = e.target?.result
      const output = parseCsv(text)
      if (output === null) {
        return null
      }
      const values: IFulfilmentCSVData[] = output
      const requestBody: IUploadReqBody = { preorders: [] }
      requestBody[`preorders`] = values
      setUploadReqBody(requestBody)
    }

    reader.readAsText(file)
    setUploadedFilename(fileName)
  }

  const onConfirmUpload = async () => {
    // TODO: connect to API
    // let API

    try {
      toast.success('Upload success')
    } catch (error) {
      toast.error('Upload failed')
    }
  }

  const UploadBoxEmpty = () => (
    <>
      {(uploadStatus === '' || uploadStatus === 'ERROR') && (
        <>
          <DottedBox>
            <>
              <label htmlFor={`upload-preorder`} style={{ cursor: 'pointer' }}>
                <Box display='flex' alignItems='center' justifyContent='center'>
                  <Typography
                    sx={{
                      color: '#0000FF',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      marginRight: '4px'
                    }}
                  >
                    Select File
                  </Typography>
                  <img
                    src={PlusIcon}
                    alt='upload-csv-logo'
                    width='20px'
                    height='20px'
                  />
                </Box>
              </label>
              <input
                id={`upload-preorder`}
                type='file'
                accept='.csv'
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  if (!e.target.files) return
                  onFileUpload(e.target.files[0])
                }}
                hidden
              />
            </>
          </DottedBox>
          <Typography variant='subtitle1' sx={{ color: '#B8C4D3' }}>
            xlsx / csv, max 8mb
          </Typography>
        </>
      )}
      {uploadedFilename && (
        <Typography>
          Uploaded file: <strong>{uploadedFilename}</strong>
        </Typography>
      )}
      <Typography variant='subtitle1' sx={{ color: 'red' }}>
        {uploadStatus === 'ERROR' && uploadErr}
      </Typography>
    </>
  )

  useEffect(() => {
    if (uploadReqBody.preorders.length > 0) {
      setRows(
        uploadReqBody.preorders.map((value: IFulfilmentCSVData, index) => {
          return { ...value, isNew: true, id: String(index) }
        })
      )
    }
  }, [uploadReqBody])

  return (
    <Grid item>
      <ColorBoxContent>
        <Typography variant='h4' sx={{ mb: 4 }}>
          Upload Preorder CSV
        </Typography>
        <Typography variant='h5' sx={{ mb: 2 }}>
          Please note that the fulfillment table may take up to 15 min to
          update.
        </Typography>
        <Stepper orientation='vertical' sx={stepIconSx}>
          <Step active>
            <StepLabel>
              <StrongText>Upload CSV for peorder fulfilment here</StrongText>
            </StepLabel>
            <StepContent>
              <UploadBoxEmpty />
            </StepContent>
          </Step>
          <Step active={uploadStatus === 'PENDING' || uploadStatus === 'VALID'}>
            <StepLabel>
              <StrongText>Check Upload Contents</StrongText>
            </StepLabel>
            <StepContent>
              {uploadedFilename && (
                <FulfilmentUploadDataGrid
                  {...{
                    rows,
                    setRows
                  }}
                />
              )}
              {uploadStatus === 'PENDING' && (
                <div>
                  <Button
                    variant='contained'
                    onClick={() => {
                      setUploadStatus('VALID')
                    }}
                    sx={{ mt: 1, mr: 1 }}
                  >
                    Continue
                  </Button>
                </div>
              )}
            </StepContent>
          </Step>
          <Step active={uploadStatus === 'VALID'}>
            <StepLabel>
              <StrongText>Upload File</StrongText>
            </StepLabel>
            <StepContent>
              <Button variant='filled' onClick={onConfirmUpload} sx={{ p: 4 }}>
                Confirm upload file: <br />
                {uploadedFilename}
              </Button>
            </StepContent>
          </Step>
        </Stepper>
      </ColorBoxContent>
    </Grid>
  )
}

interface IDottedBoxProps {
  children: React.ReactNode
}

interface IUploadReqBody {
  preorders: IFulfilmentCSVData[]
}

interface IFulfilmentCSVData {
  firstName: string
  lastName: string
  userEmail: string
  countryCode: string
  mobileNo: string
}

interface IFulfilmentRow {
  id: string
  firstName: string
  lastName: string
  userEmail: string
  countryCode: string
  mobileNo: string
  isNew: boolean
}
